'use client';
import React, { useRef, useState, useCallback } from 'react';
import Link from 'next/link';
import ROUTES from '@/lib/routes';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useAuth } from '@/providers/auth';
import { useEffect } from 'react';
import { isMobile } from '@flyer/utils';
import dynamic from 'next/dynamic';
import { twMerge } from 'tailwind-merge';
import useSignUpFlowModal from '@/components/popups/auth/useSignUpFlowModal';
import useForgotPasswordModal from '@/components/popups/auth/useForgotPasswordModal';
import { useModal } from '@flyer/components';
import { SignInModalLazy } from '@/components/popups';

interface UserProfilePopupProps {
  isCollapsed: boolean;
  onChangeInterface?: () => void;
}

export default function UserProfilePopup({
  isCollapsed,
  onChangeInterface,
}: UserProfilePopupProps) {
  const { t: ieltsTranslation } = useTranslation('ielts');
  const [showUserPopup, setShowUserPopup] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const userPopupRef = useRef<HTMLDivElement>(null);
  const userAvatarRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { currentLearnerProfile, user, logOut, isLogin } = useAuth();
  const IconDiamond = dynamic(() => import('@/assets/images/common/icon-diamond.svg'), {
    ssr: false,
  });
  const { openSignUp } = useSignUpFlowModal();
  const { openForgotPassword } = useForgotPasswordModal();
  const { toggleModal: toggleSignIn, hideModal: hideSignIn } = useModal(SignInModalLazy);

  const handleOpenSignIn = useCallback(() => {
    toggleSignIn({
      onSignUpClick: () => {
        hideSignIn();
        openSignUp(() => {
          toggleSignIn({
            onSignUpClick: () => {
              hideSignIn();
              openSignUp(() => handleOpenSignIn());
            },
            onForgotPasswordClick: () => {
              hideSignIn();
              openForgotPassword({ onSignInClick: () => handleOpenSignIn() });
            },
          });
        });
      },
      onForgotPasswordClick: () => {
        hideSignIn();
        openForgotPassword({ onSignInClick: () => handleOpenSignIn() });
      },
    });
  }, [toggleSignIn, hideSignIn, openSignUp, openForgotPassword]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const goToPackage = useCallback(() => router.push(ROUTES.FLYER_PACKAGE), [router]);

  const handleEditProfile = useCallback(() => {
    router.push(ROUTES.EDIT_PROFILE_CLASSIC);
    setShowUserPopup(false);
  }, [router]);

  const handleSwitchProfile = useCallback(() => {
    router.push('/profiles');
    setShowUserPopup(false);
  }, [router]);

  const handleChangeInterface = useCallback(() => {
    setShowUserPopup(false);
    router.push(ROUTES.EDIT_THEME_CLASSIC);
  }, [router]);

  const handleActiveCode = useCallback(() => {
    router.push(ROUTES.ACTIVE_ACCOUNT);
    setShowUserPopup(false);
  }, [router]);

  const toggleUserPopup = useCallback(() => {
    if (!isLogin) {
      handleOpenSignIn();
      return;
    }
    setShowUserPopup(!showUserPopup);
  }, [showUserPopup, isLogin, handleOpenSignIn]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        userPopupRef.current &&
        !userPopupRef.current.contains(event.target as Node) &&
        userAvatarRef.current &&
        !userAvatarRef.current.contains(event.target as Node)
      ) {
        setShowUserPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Trả về một phiên bản đơn giản khi chưa mount ở client
  if (!isMounted) {
    return (
      <div className="flex items-center gap-3 p-1 cursor-pointer relative">
        <div className="w-[28px] h-[28px] md:w-[47px] md:h-[47px] rounded-full overflow-hidden border-[1px] border-ielts-tint-line p-[1px] md:p-[3px]">
          <div className="bg-ielts-surface rounded-full w-full h-full overflow-hidden"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 p-1 cursor-pointer relative mb-0 md:mb-3"
      onClick={toggleUserPopup}
      ref={userAvatarRef}
    >
      <div className="w-[28px] h-[28px] md:w-[47px] md:h-[47px] rounded-full overflow-hidden border-[1px] border-ielts-tint-line p-[1px] md:p-[3px]">
        <div className="bg-ielts-surface rounded-full w-full h-full overflow-hidden">
          {currentLearnerProfile?.avatarUrl ? (
            <img
              src={currentLearnerProfile.avatarUrl}
              alt="User profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-ielts-accent-bg text-ielts-on-accent flex items-center justify-center font-bold">
              {user?.email?.charAt(0) || 'U'}
            </div>
          )}
        </div>
      </div>
      {isMounted && !isMobile() && (
        <>
          <span
            className={`font-medium text-ielts-fg ${
              isCollapsed ? 'hidden opacity-0 max-w-0' : 'block opacity-100 max-w-[120px]'
            } overflow-hidden transition-all duration-500 ease-in-out whitespace-nowrap`}
          >
            {isLogin
              ? currentLearnerProfile?.displayName || user?.email || 'User'
              : ieltsTranslation('homepage.menuSignIn')}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--ielts-fg)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`w-5 h-5 ml-auto ${isCollapsed ? 'hidden opacity-0 max-w-0' : 'block opacity-100'} transition-all duration-500 ease-in-out`}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </>
      )}

      {/* User Popup */}
      {showUserPopup && isLogin && (
        <div
          className={`absolute  mt-2 w-64 bg-ielts-surface rounded-[10px] shadow-lg z-50 overflow-hidden border-[1px] border-ielts-line ${isMobile() ? 'right-[0] top-[100%]' : 'left-[105%] bottom-[100%]'}`}
          ref={userPopupRef}
        >
          {/* Diamond info section */}
          <div className="p-[6px] border-[1px] border-ielts-tint-line bg-ielts-tint rounded-[10px] m-[4px]">
            <Link
              href="/packages"
              className="block w-full bg-ielts-accent-bg text-ielts-on-accent text-center py-3 rounded-[6px] font-medium mb-4 opacity-100 hover:opacity-90"
              onClick={goToPackage}
            >
              {ieltsTranslation('homepage.textUpgrade')}
            </Link>

            <Link href={ROUTES.DIAMONDS_PREVIEW} className="block">
              <div className="flex justify-between items-center mb-2 pl-[6px]">
                <span className="text-ielts-fg-secondary text-[14px] font-[600]">
                  {ieltsTranslation('homepage.textTotal')}
                </span>
                <div className="flex items-center gap-[2px]">
                  <span className={twMerge('w-[21px] h-[21px] mr-1')}>
                    <IconDiamond />
                  </span>
                  <span className="font-[600] text-[14px] text-ielts-fg">
                    {(user?.diamond || 0) + (user?.freeIeltsDiamond || 0)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center mb-2 pl-[6px]">
                <span className="text-ielts-fg-secondary text-[14px] font-[600]">
                  {ieltsTranslation('homepage.textDiamondBuy')}
                </span>
                <div className="flex items-center gap-[2px]">
                  <span className={twMerge('w-[21px] h-[21px] mr-1')}>
                    <IconDiamond />
                  </span>
                  <span className="font-[600] text-[14px] text-ielts-fg">
                    {user?.diamond || 0}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center mb-2 pl-[6px]">
                <span className="text-ielts-fg-secondary text-[14px] font-[600]">
                  {ieltsTranslation('homepage.textDiamondGift')}
                </span>
                <div className="flex items-center gap-[2px]">
                  <span className={twMerge('w-[21px] h-[21px] mr-1')}>
                    <IconDiamond />
                  </span>
                  <span className="font-[600] text-[14px] text-ielts-fg">
                    {user?.freeIeltsDiamond || 0}
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Menu options */}
          <div className="py-1 border-t-[1px] border-ielts-line pb-[12px]">
            <button
              className="w-full text-left px-4 py-2 text-ielts-fg-secondary hover:bg-ielts-tint hover:text-ielts-accent flex items-center font-[600] text-[14px]"
              onClick={handleChangeInterface}
              type="button"
            >
              {ieltsTranslation('homepage.textChangeInterface')}
            </button>

            <button
              className="w-full text-left px-4 py-2 text-ielts-fg-secondary hover:bg-ielts-tint hover:text-ielts-accent flex items-center font-[600] text-[14px]"
              onClick={handleActiveCode}
              type="button"
            >
              {ieltsTranslation('homepage.textActiveCode')}
            </button>

            <button
              type="button"
              className="w-full text-left px-4 py-2 text-ielts-fg-secondary hover:bg-ielts-tint hover:text-ielts-accent flex items-center justify-between font-[600] text-[14px]"
              onClick={handleSwitchProfile}
            >
              <span>{ieltsTranslation('homepage.textSwitchProfile')}</span>
              <div className="flex items-center gap-2">
                <div className="w-[24px] h-[24px] rounded-full overflow-hidden border-[1px] border-ielts-tint-line">
                  {currentLearnerProfile?.avatarUrl ? (
                    <img
                      src={currentLearnerProfile.avatarUrl}
                      alt="User profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-ielts-accent-bg text-ielts-on-accent flex items-center justify-center text-[10px]">
                      {user?.email?.charAt(0) || 'U'}
                    </div>
                  )}
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-chevron-right text-ielts-fg-muted"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </div>
            </button>

            <button
              className="w-full text-left px-4 py-2 text-ielts-fg-secondary hover:bg-ielts-tint hover:text-ielts-accent flex items-center font-[600] text-[14px]"
              onClick={handleEditProfile}
              type="button"
            >
              {ieltsTranslation('homepage.textEditProfile')}
            </button>
            <button
              className="w-full text-left px-4 py-2 text-ielts-fg-secondary hover:bg-ielts-tint hover:text-ielts-accent flex items-center font-[600] text-[14px]"
              onClick={() => logOut(true)}
              type="button"
            >
              {ieltsTranslation('homepage.textLogout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
